import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react'
import styles from './NPATab.module.scss'
import DocTable from '../../components/DocTable/DocTable'
import SearchBar from '../../components/SearchBar/SearchBar'
import {
  getNPAFolders,
  getNPAList,
  downloadNPA,
} from 'service/KomurConnectionService'
import { filterDocs } from '../../utils'

// ── иконки файлов по расширению ──────────────────────────────────────────────
function folderDocIcon(count) {
  if (count === 0) return '📂'
  return '📁'
}

// ── Карточка папки ───────────────────────────────────────────────────────────
function FolderCard({ folder, onClick }) {
  useInterfaceLanguage();
  return (
    <div
      className={styles.folderCard}
      onClick={() => onClick(folder)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick(folder)}
      style={{ '--folder-color': folder.color || "var(--theme-info-text, #2563eb)" }}
    >
      <div className={styles.folderIconWrap}>
        <span className={styles.folderEmoji}>{tr(folder.icon || '📁')}</span>
      </div>
      <div className={styles.folderInfo}>
        <span className={styles.folderName}>{tr(folder.name)}</span>
        {tr(folder.description && (
          <span className={styles.folderDesc}>{tr(folder.description)}</span>
        ))}
      </div>
      <div className={styles.folderMeta}>
        <span className={styles.folderCount}>{tr(folder.doc_count)}</span>
        <span className={styles.folderCountLabel}>
          {folder.doc_count === 1 ? tr("документ") : folder.doc_count >= 2 && folder.doc_count <= 4 ? tr("документа") : tr("документов")}
        </span>
        <span className={styles.folderArrow}>›</span>
      </div>
    </div>
  )
}

// ── Основной компонент ───────────────────────────────────────────────────────
export default function NPATab({ onPreview }) {
  useInterfaceLanguage();
  const [folders, setFolders] = useState(null)
  const [looseDocs, setLooseDocs] = useState(null)      // без папки
  const [openFolder, setOpenFolder] = useState(null)    // { folder, docs }
  const [folderLoading, setFolderLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [view, setView] = useState('grid')              // 'grid' | 'folder'

  // Загружаем папки и документы без папки
  useEffect(() => {
    getNPAFolders()
      .then(r => setFolders(r.data))
      .catch(() => setFolders([]))

    getNPAList({ folder: 'none' })
      .then(r => setLooseDocs(r.data))
      .catch(() => setLooseDocs([]))
  }, [])

  const handleOpenFolder = useCallback(async (folder) => {
    setFolderLoading(true)
    setOpenFolder({ folder, docs: null })
    setView('folder')
    setSearch('')
    try {
      const r = await getNPAList({ folder: folder.id })
      setOpenFolder({ folder, docs: r.data })
    } catch {
      setOpenFolder({ folder, docs: [] })
    } finally {
      setFolderLoading(false)
    }
  }, [])

  const handleBack = () => {
    setView('grid')
    setOpenFolder(null)
    setSearch('')
  }

  const handleDownload = (_docType, id, fileName) => {
    downloadNPA(id).then(r => {
      const a = document.createElement('a')
      a.href = r.data.file_url
      a.download = fileName || 'document'
      a.click()
    }).catch(() => {})
  }

  // ── Вид: открытая папка ─────────────────────────────────────────────────
  if (view === 'folder' && openFolder) {
    const { folder, docs } = openFolder
    return (
      <div className={styles.section}>
        <div className={styles.folderHeader}>
          <button className={styles.backBtn} onClick={handleBack}>{tr("‹ Назад")}</button>
          <div className={styles.folderHeaderInfo}>
            <span className={styles.folderHeaderIcon} style={{ color: folder.color }}>
              {tr(folder.icon || '📁')}
            </span>
            <div>
              <h3 className={styles.title}>{tr(folder.name)}</h3>
              {tr(folder.description && (
                <p className={styles.folderHeaderDesc}>{tr(folder.description)}</p>
              ))}
            </div>
          </div>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder={tr("Поиск в папке…")} />
        {folderLoading
          ? <p className={styles.status}>{tr("Загрузка…")}</p>
          : <DocTable
              docs={filterDocs(docs, search)}
              docType="npa"
              onDownload={handleDownload}
              onPreview={onPreview}
            />
        }
      </div>
    )
  }

  // ── Вид: сетка папок ────────────────────────────────────────────────────
  const hasFolders = folders && folders.length > 0
  const hasLoose   = looseDocs && looseDocs.length > 0
  const filteredLoose = filterDocs(looseDocs, search)

  return (
    <div className={styles.section}>
      <h3 className={styles.title}>{tr("Нормативно-правовые акты")}</h3>

      {/* Папки */}
      {folders === null ? (
        <p className={styles.status}>{tr("Загрузка…")}</p>
      ) : hasFolders ? (
        <div className={styles.foldersGrid}>
          {folders.map(folder => (
            <FolderCard key={folder.id} folder={folder} onClick={handleOpenFolder} />
          ))}
        </div>
      ) : null}

      {/* Документы без папки */}
      {tr(hasLoose && (
        <div className={styles.looseSection}>
          <div className={styles.looseSectionHeader}>
            <span className={styles.looseSectionIcon}>📄</span>
            <span className={styles.looseSectionTitle}>{tr("Документы")}</span>
            <span className={styles.looseSectionCount}>{tr(looseDocs.length)}</span>
          </div>
          <SearchBar value={search} onChange={setSearch} placeholder={tr("Поиск НПА…")} />
          <DocTable
            docs={filteredLoose}
            docType="npa"
            onDownload={handleDownload}
            onPreview={onPreview}
          />
        </div>
      ))}

      {/* Пусто */}
      {tr(!hasFolders && !hasLoose && folders !== null && looseDocs !== null && (
        <p className={styles.status}>{tr("Нет документов")}</p>
      ))}
    </div>
  )
}
